import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import { ToastContainer, ToastType } from '../components/Toast';
import {
  FolderIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
  LinkIcon,
  PhotoIcon,
  HandThumbUpIcon,
  HandThumbDownIcon,
  ClockIcon,
  PlusIcon,
  XCircleIcon,
  HandRaisedIcon,
} from '@heroicons/react/24/outline';

const CARD_BG = '#1c2432';

// Interfaces
interface Project {
  id: number;
  name: string;
  description: string;
  status: string;
  created_at: string;
  creator_name: string;
  image_path?: string;
  voting_deadline?: string;
  min_price?: number;
  max_price?: number;
  variant_count?: number;
  helper_count?: number;
  items?: ProjectItem[];
}

interface ProjectItem {
  id: number;
  project_id: number;
  name: string;
  description: string;
  quantity: number;
  position_order: number;
  variants?: ItemVariant[];
}

interface ItemVariant {
  id: number;
  project_item_id: number;
  name: string;
  description: string;
  price: number | string;
  image_path?: string;
  link?: string;
  vote_count: number;
  total_votes: number;
  my_vote?: number;
  creator_name?: string;
}

const MyProjects: React.FC = () => {
  const { BASE_URL } = useContext(AuthContext);

  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(false);
  const [helperStatusMap, setHelperStatusMap] = useState<Record<number, boolean>>({});
  const [helperLoadingMap, setHelperLoadingMap] = useState<Record<number, boolean>>({});

  // Toast-System
  const [toasts, setToasts] = useState<Array<{ id: string; type: ToastType; message: string }>>([]);

  const addToast = (type: ToastType, message: string) => {
    const id = Date.now().toString();
    setToasts(prev => [...prev, { id, type, message }]);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  // Grid Layout State - Anzahl der Spalten für Varianten-Ansicht (2-6)
  const [gridColumns, setGridColumns] = useState<number>(() => {
    const saved = localStorage.getItem('myProjectsGridColumns');
    return saved ? parseInt(saved, 10) : 3;
  });

  const handleGridColumnsChange = (columns: number) => {
    setGridColumns(columns);
    localStorage.setItem('myProjectsGridColumns', columns.toString());
  };

  // Grid class helper
  const getGridClass = (): string => {
    const baseClass = 'grid grid-cols-1';

    let mdClass = 'md:grid-cols-2';
    if (gridColumns === 3) mdClass = 'md:grid-cols-3';

    let lgClass = '';
    switch(gridColumns) {
      case 2: lgClass = 'lg:grid-cols-2'; break;
      case 3: lgClass = 'lg:grid-cols-3'; break;
      case 4: lgClass = 'lg:grid-cols-4'; break;
      case 5: lgClass = 'lg:grid-cols-4 xl:grid-cols-5'; break;
      case 6: lgClass = 'lg:grid-cols-4 xl:grid-cols-6'; break;
      default: lgClass = 'lg:grid-cols-3';
    }

    return `${baseClass} ${mdClass} ${lgClass}`;
  };

  const getCardSpacing = (): string => {
    if (gridColumns <= 3) return 'p-5';
    if (gridColumns === 4) return 'p-4';
    return 'p-3';
  };

  const getGridGap = (): string => {
    if (gridColumns <= 3) return 'gap-6';
    if (gridColumns === 4) return 'gap-4';
    return 'gap-3';
  };

  const getImageHeight = (): string => {
    if (gridColumns <= 3) return 'h-56';
    if (gridColumns === 4) return 'h-48';
    return 'h-40';
  };

  const getTextSizes = () => ({
    name: gridColumns <= 4 ? 'text-lg' : 'text-base',
    price: gridColumns <= 4 ? 'text-lg' : 'text-base',
    description: gridColumns <= 4 ? 'text-sm' : 'text-xs',
  });

  // States für Varianten-Modal
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ProjectItem | null>(null);
  const [variantForm, setVariantForm] = useState({
    name: '',
    description: '',
    price: '',
    link: ''
  });
  const [selectedVariantImage, setSelectedVariantImage] = useState<File | null>(null);
  const [variantImagePreview, setVariantImagePreview] = useState<string | null>(null);

  const isVotingClosed = (deadline?: string): boolean => {
    if (!deadline) return false;
    return new Date(deadline) < new Date();
  };

  const getRemainingTime = (deadline?: string): string => {
    if (!deadline) return '';

    const now = new Date();
    const end = new Date(deadline);
    const diff = end.getTime() - now.getTime();

    if (diff <= 0) return 'Abstimmung beendet';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) return `Noch ${days} Tag${days > 1 ? 'e' : ''} und ${hours} Stunden`;
    if (hours > 0) return `Noch ${hours} Stunde${hours > 1 ? 'n' : ''} und ${minutes} Minuten`;
    return `Noch ${minutes} Minute${minutes > 1 ? 'n' : ''}`;
  };

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/projects');
      if (response.data.success) {
        const activeProjects = response.data.projects.filter((p: Project) => p.status === 'active');
        setProjects(activeProjects);
        loadAllHelperStatuses(activeProjects);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Projekte:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProjectDetails = async (projectId: number) => {
    try {
      const response = await apiClient.get(`/api/projects/${projectId}`);
      if (response.data.success) {
        setSelectedProject(response.data.project);
        loadHelperStatus(projectId);
      }
    } catch (error) {
      console.error('Fehler beim Laden des Projekts:', error);
    }
  };

  const loadAllHelperStatuses = async (projects: Project[]) => {
    const statusMap: Record<number, boolean> = {};

    for (const project of projects) {
      try {
        const response = await apiClient.get(`/api/projects/${project.id}/helpers/me`);
        if (response.data.success) {
          statusMap[project.id] = response.data.isHelper;
        }
      } catch (error) {
        console.error(`Fehler beim Laden des Helfer-Status für Projekt ${project.id}:`, error);
      }
    }

    setHelperStatusMap(statusMap);
  };

  const loadHelperStatus = async (projectId: number) => {
    try {
      const response = await apiClient.get(`/api/projects/${projectId}/helpers/me`);
      if (response.data.success) {
        setHelperStatusMap(prev => ({ ...prev, [projectId]: response.data.isHelper }));
      }
    } catch (error) {
      console.error(`Fehler beim Laden des Helfer-Status für Projekt ${projectId}:`, error);
    }
  };

  const toggleHelperStatus = async (projectId: number, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }

    setHelperLoadingMap(prev => ({ ...prev, [projectId]: true }));
    const currentStatus = helperStatusMap[projectId] || false;

    try {
      if (currentStatus) {
        const response = await apiClient.delete(`/api/projects/${projectId}/helpers`);
        if (response.data.success) {
          setHelperStatusMap(prev => ({ ...prev, [projectId]: false }));
          addToast('success', 'Sie haben sich erfolgreich abgemeldet');
          loadProjects();
        }
      } else {
        const response = await apiClient.post(`/api/projects/${projectId}/helpers`, {});
        if (response.data.success) {
          setHelperStatusMap(prev => ({ ...prev, [projectId]: true }));
          addToast('success', 'Sie haben sich erfolgreich als Helfer registriert');
          loadProjects();
        }
      }
    } catch (error) {
      console.error('Fehler beim Ändern des Helfer-Status:', error);
      addToast('error', 'Es ist ein Fehler aufgetreten');
    } finally {
      setHelperLoadingMap(prev => ({ ...prev, [projectId]: false }));
    }
  };

  const handleVote = async (variantId: number, voteValue: number) => {
    try {
      const response = await apiClient.post(`/api/projects/variants/${variantId}/vote`, { vote_value: voteValue });
      if (response.data.success && selectedProject) {
        loadProjectDetails(selectedProject.id);
      }
    } catch (error) {
      console.error('Fehler beim Abstimmen:', error);
    }
  };

  const openAddVariant = (item: ProjectItem) => {
    setSelectedItem(item);
    setVariantForm({ name: '', description: '', price: '', link: '' });
    setSelectedVariantImage(null);
    setVariantImagePreview(null);
    setShowVariantModal(true);
  };

  const handleVariantImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedVariantImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setVariantImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleVariantPaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (items) {
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          const file = items[i].getAsFile();
          if (file) {
            setSelectedVariantImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
              setVariantImagePreview(reader.result as string);
            };
            reader.readAsDataURL(file);
          }
          break;
        }
      }
    }
  };

  const handleSaveVariant = async () => {
    if (!selectedItem) return;

    try {
      const formData = new FormData();
      formData.append('name', variantForm.name);
      formData.append('description', variantForm.description);
      formData.append('price', variantForm.price);
      formData.append('link', variantForm.link);

      if (selectedVariantImage) {
        formData.append('image', selectedVariantImage);
      }

      const response = await apiClient.post(`/api/projects/items/${selectedItem.id}/variants`, formData);

      if (response.data.success) {
        if (selectedProject) {
          loadProjectDetails(selectedProject.id);
        }
        setShowVariantModal(false);
        setVariantForm({ name: '', description: '', price: '', link: '' });
        setSelectedVariantImage(null);
        setVariantImagePreview(null);
        addToast('success', 'Variante erfolgreich hinzugefügt!');
      }
    } catch (error: any) {
      console.error('Fehler beim Speichern der Variante:', error);
      const errorMessage = error.response?.data?.message || 'Ein unbekannter Fehler ist aufgetreten';
      addToast('error', errorMessage);
    }
  };

  const toNumber = (value: string | number | undefined | null): number => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return value;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  };

  const getImageUrl = (imagePath: string | undefined): string | undefined => {
    if (!imagePath) return undefined;
    if (imagePath.startsWith('http')) return imagePath;
    return `${BASE_URL}/api/projects/image/${encodeURIComponent(imagePath)}`;
  };

  // Stats für Hero
  const totalProjects = projects.length;
  const totalHelpers = projects.reduce((sum, p) => sum + (p.helper_count || 0), 0);
  const openVotings = projects.filter(p => p.voting_deadline && !isVotingClosed(p.voting_deadline)).length;
  const myHelpCount = Object.values(helperStatusMap).filter(Boolean).length;
  const totalVariants = projects.reduce((sum, p) => sum + (p.variant_count || 0), 0);

  const monthYearLabel = new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });

  return (
    <div className="min-h-screen font-sans text-gray-900 dark:text-slate-200" style={{ background: 'transparent' }}>
      <div className="max-w-7xl mx-auto py-2 sm:py-4">

        {/* HERO */}
        {!selectedProject && (
          <section className="pt-2 pb-6">
            <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 items-end">
              <div className="lg:col-span-8">
                <div className="text-[11px] uppercase tracking-[0.22em] text-primary-600/80 dark:text-primary-400/80 font-semibold mb-3">
                  Projekte · Stand {monthYearLabel}
                </div>
                <h1
                  className="text-4xl sm:text-5xl md:text-6xl font-black text-gray-900 dark:text-white leading-[1.05] tracking-tight"
                  style={{ letterSpacing: '-0.04em' }}
                >
                  Vereins-<br />
                  <span className="text-primary-600 dark:text-primary-400">Projekte.</span>
                </h1>
                <p className="text-gray-700 dark:text-slate-300 text-base md:text-lg leading-relaxed max-w-2xl mt-4">
                  {totalProjects === 0 ? (
                    <>Noch keine aktiven Projekte angelegt. Sobald der Vorstand ein Projekt startet, kannst du hier <span className="font-bold text-gray-900 dark:text-white">mithelfen und abstimmen</span>.</>
                  ) : (
                    <>
                      <span className="font-bold text-gray-900 dark:text-white">{totalProjects} {totalProjects === 1 ? 'aktives Projekt' : 'aktive Projekte'}</span>
                      {totalVariants > 0 && (
                        <> · <span className="font-bold text-gray-900 dark:text-white">{totalVariants}</span> {totalVariants === 1 ? 'Variante' : 'Varianten'} zur Auswahl</>
                      )}
                      {openVotings > 0 && (
                        <> · <span className="font-bold text-amber-700 dark:text-amber-300">{openVotings}</span> {openVotings === 1 ? 'offene Abstimmung' : 'offene Abstimmungen'}</>
                      )}
                      {totalHelpers > 0 && (
                        <> · <span className="font-bold text-primary-700 dark:text-primary-300">{totalHelpers}</span> {totalHelpers === 1 ? 'Helfer' : 'Helfer:innen'} eingetragen</>
                      )}
                      {myHelpCount > 0 && (
                        <> · Du hilfst bei <span className="font-bold text-primary-700 dark:text-primary-300">{myHelpCount}</span> {myHelpCount === 1 ? 'Projekt' : 'Projekten'} mit. Danke!</>
                      )}
                      .
                    </>
                  )}
                </p>
              </div>
              <div className="lg:col-span-4 grid grid-cols-3 gap-3">
                <div className="rounded-2xl p-3 lg:p-4 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700">
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Aktiv</div>
                  <div className="text-2xl lg:text-3xl font-black text-gray-900 dark:text-white mt-1 tabular-nums">{totalProjects}</div>
                  <div className="text-[10px] text-gray-600 dark:text-slate-400 mt-0.5">{totalProjects === 1 ? 'Projekt' : 'Projekte'}</div>
                </div>
                <div className="rounded-2xl p-3 lg:p-4 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700">
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Voting</div>
                  <div className="text-2xl lg:text-3xl font-black text-amber-700 dark:text-amber-300 mt-1 tabular-nums">{openVotings}</div>
                  <div className="text-[10px] text-gray-600 dark:text-slate-400 mt-0.5">offen</div>
                </div>
                <div className="rounded-2xl p-3 lg:p-4 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700">
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Helfer</div>
                  <div className="text-2xl lg:text-3xl font-black text-primary-600 dark:text-primary-400 mt-1 tabular-nums">{totalHelpers}</div>
                  <div className="text-[10px] text-gray-600 dark:text-slate-400 mt-0.5">eingetragen</div>
                </div>
              </div>
            </div>
          </section>
        )}

        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
          </div>
        )}

        {/* Projects List */}
        {!selectedProject ? (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {projects.map((project) => (
                <article
                  key={project.id}
                  className="relative rounded-3xl overflow-hidden cursor-pointer transition hover:-translate-y-0.5 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                  style={{
                    boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)',
                  }}
                  onClick={() => loadProjectDetails(project.id)}
                >
                  <div className="relative h-56 bg-slate-800 overflow-hidden">
                    {project.image_path ? (
                      <>
                        <img
                          src={getImageUrl(project.image_path)}
                          alt={project.name}
                          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                          onError={(e) => {
                            const img = e.target as HTMLImageElement;
                            img.style.display = 'none';
                            const fallback = img.nextElementSibling as HTMLElement;
                            if (fallback) fallback.style.display = 'flex';
                          }}
                        />
                        <div className="w-full h-full flex items-center justify-center" style={{ display: 'none' }}>
                          <FolderIcon className="h-24 w-24 text-slate-600" />
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <FolderIcon className="h-24 w-24 text-slate-600" />
                      </div>
                    )}
                  </div>
                  <div className="p-5 sm:p-6">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">{project.name}</h3>
                    <p className="mt-2 text-gray-700 dark:text-slate-300 text-sm leading-relaxed line-clamp-2">{project.description}</p>

                    {(project.variant_count ?? 0) > 0 && (
                      <div className="mt-4 rounded-2xl p-4 bg-gray-100 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08]">
                        <div className="flex items-center justify-between text-xs text-gray-600 dark:text-slate-400 mb-2">
                          <span className="uppercase tracking-widest text-[10px]">Preisspanne</span>
                          <span>{project.variant_count} {project.variant_count === 1 ? 'Variante' : 'Varianten'}</span>
                        </div>
                        <div className="flex items-baseline gap-2 flex-wrap text-gray-900 dark:text-white tabular-nums">
                          {project.min_price === project.max_price ? (
                            <span className="text-xl font-bold">{toNumber(project.min_price).toFixed(2)} €</span>
                          ) : (
                            <>
                              <span className="text-lg font-bold">{toNumber(project.min_price).toFixed(2)} €</span>
                              <span className="text-gray-400 dark:text-slate-500">—</span>
                              <span className="text-lg font-bold">{toNumber(project.max_price).toFixed(2)} €</span>
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="mt-4 flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
                        style={{ background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.30)', color: '#86efac' }}>
                        <span className="w-1.5 h-1.5 rounded-full bg-primary-400 animate-pulse" /> Aktiv
                      </span>
                      {project.voting_deadline && (
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium"
                          style={isVotingClosed(project.voting_deadline)
                            ? { background: 'rgba(244,63,94,0.12)', border: '1px solid rgba(244,63,94,0.30)', color: '#fda4af' }
                            : { background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.30)', color: '#93c5fd' }}>
                          <ClockIcon className="h-3 w-3" />
                          {getRemainingTime(project.voting_deadline)}
                        </span>
                      )}
                      {(project.helper_count ?? 0) > 0 && (
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium"
                          style={{ background: 'rgba(34,197,94,0.10)', border: '1px solid rgba(34,197,94,0.25)', color: '#86efac' }}>
                          <HandRaisedIcon className="h-3 w-3" />
                          {project.helper_count} {project.helper_count === 1 ? 'Helfer' : 'Helfer'}
                        </span>
                      )}
                    </div>

                    <div className="mt-4">
                      <button
                        onClick={(e) => toggleHelperStatus(project.id, e)}
                        disabled={helperLoadingMap[project.id]}
                        className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition ${
                          helperStatusMap[project.id]
                            ? 'bg-primary-500/15 border border-primary-400/40 text-primary-200 hover:bg-primary-500/25'
                            : 'bg-primary-500 hover:bg-primary-400 text-slate-950'
                        } ${helperLoadingMap[project.id] ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {helperLoadingMap[project.id] ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current flex-shrink-0"></div>
                            <span>Lädt...</span>
                          </>
                        ) : (
                          <>
                            <HandRaisedIcon className="h-5 w-5 flex-shrink-0" />
                            <span>{helperStatusMap[project.id] ? 'Sie helfen mit' : 'Ich möchte mithelfen'}</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {projects.length === 0 && !loading && (
              <div className="rounded-3xl px-6 py-12 text-center bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700">
                <FolderIcon className="h-16 w-16 mx-auto text-gray-400 dark:text-slate-600 mb-4" />
                <p className="text-gray-700 dark:text-slate-300">Keine aktiven Projekte vorhanden.</p>
                <p className="text-gray-500 dark:text-slate-500 text-sm mt-1">Sobald ein neues Projekt gestartet wird, erscheint es hier.</p>
              </div>
            )}
          </>
        ) : (
          /* Project Details View */
          <div className="space-y-6">
            <button
              onClick={() => setSelectedProject(null)}
              className="inline-flex items-center gap-2 text-primary-300 hover:text-primary-200 text-sm font-semibold"
            >
              ← Zurück zur Übersicht
            </button>

            {/* Project Header Card */}
            <article
              className="rounded-3xl overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              style={{
                boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)',
              }}
            >
              <div className="relative h-64 bg-slate-800 overflow-hidden">
                {selectedProject.image_path ? (
                  <>
                    <img
                      src={getImageUrl(selectedProject.image_path)}
                      alt={selectedProject.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const img = e.target as HTMLImageElement;
                        img.style.display = 'none';
                        const fallback = img.nextElementSibling as HTMLElement;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                    <div className="w-full h-full flex items-center justify-center" style={{ display: 'none' }}>
                      <FolderIcon className="h-32 w-32 text-slate-600" />
                    </div>
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <FolderIcon className="h-32 w-32 text-slate-600" />
                  </div>
                )}
              </div>
              <div className="p-6">
                <h2 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white tracking-tight">{selectedProject.name}</h2>
                <p className="mt-3 text-gray-700 dark:text-slate-300 leading-relaxed">{selectedProject.description}</p>
                <p className="mt-2 text-sm text-gray-500 dark:text-slate-500">Erstellt von: <span className="text-gray-700 dark:text-slate-300">{selectedProject.creator_name}</span></p>

                <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
                  {selectedProject.voting_deadline && (
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold"
                      style={isVotingClosed(selectedProject.voting_deadline)
                        ? { background: 'rgba(244,63,94,0.12)', border: '1px solid rgba(244,63,94,0.30)', color: '#fda4af' }
                        : { background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.30)', color: '#93c5fd' }}>
                      <ClockIcon className="h-5 w-5" />
                      <span>{getRemainingTime(selectedProject.voting_deadline)}</span>
                    </div>
                  )}

                  <button
                    onClick={() => toggleHelperStatus(selectedProject.id)}
                    disabled={helperLoadingMap[selectedProject.id]}
                    className={`w-full sm:w-auto sm:ml-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition ${
                      helperStatusMap[selectedProject.id]
                        ? 'bg-primary-500/15 border border-primary-400/40 text-primary-200 hover:bg-primary-500/25'
                        : 'bg-primary-500 hover:bg-primary-400 text-slate-950'
                    } ${helperLoadingMap[selectedProject.id] ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {helperLoadingMap[selectedProject.id] ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current flex-shrink-0"></div>
                        <span>Lädt...</span>
                      </>
                    ) : (
                      <>
                        <HandRaisedIcon className="h-5 w-5 flex-shrink-0" />
                        <span>{helperStatusMap[selectedProject.id] ? 'Sie helfen bereits mit' : 'Ich möchte mithelfen'}</span>
                      </>
                    )}
                  </button>
                </div>

                {helperStatusMap[selectedProject.id] && (
                  <p className="mt-3 text-sm text-gray-600 dark:text-slate-400">
                    Der Vorstand wurde über deine Bereitschaft informiert.
                  </p>
                )}
              </div>
            </article>

            {/* Items List */}
            {selectedProject.items && selectedProject.items.map((item) => (
              <article
                key={item.id}
                className="rounded-3xl p-5 sm:p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              >
                <div className="flex flex-col sm:flex-row sm:items-start gap-4 mb-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2 flex-wrap">
                      <ClipboardDocumentListIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                      {item.name}
                      <span className="text-sm font-medium text-gray-600 dark:text-slate-400">({item.quantity}x)</span>
                    </h3>
                    {item.description && (
                      <p className="mt-1 text-gray-700 dark:text-slate-300 text-sm">{item.description}</p>
                    )}
                  </div>
                  {!isVotingClosed(selectedProject.voting_deadline) && (
                    <button
                      onClick={() => openAddVariant(item)}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-primary-500 hover:bg-primary-400 text-slate-950 text-sm font-semibold transition"
                    >
                      <PlusIcon className="h-5 w-5" />
                      <span>Variante hinzufügen</span>
                    </button>
                  )}
                </div>

                {item.variants && item.variants.length > 0 && (
                  <>
                    <div className="mb-4 hidden md:flex items-center gap-3 justify-end">
                      <span className="text-xs uppercase tracking-widest text-gray-500 dark:text-slate-500">Ansicht</span>
                      <div className="inline-flex gap-1 p-1 rounded-xl bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10">
                        {[2, 3, 4, 5, 6].map((cols) => (
                          <button
                            key={cols}
                            onClick={() => handleGridColumnsChange(cols)}
                            className={`min-w-[2.25rem] px-2.5 py-1.5 rounded-lg text-xs font-semibold transition ${
                              gridColumns === cols
                                ? 'bg-primary-500 text-slate-950'
                                : 'text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-white/5'
                            }`}
                            title={`${cols} Spalten anzeigen`}
                            aria-label={`${cols} Spalten anzeigen`}
                            aria-pressed={gridColumns === cols}
                          >
                            {cols}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className={`${getGridClass()} ${getGridGap()}`}>
                      {item.variants.map((variant) => {
                        const textSizes = getTextSizes();
                        return (
                          <div
                            key={variant.id}
                            className="relative rounded-2xl overflow-hidden group transition hover:-translate-y-0.5 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08]"
                          >
                            <div className={`relative ${getImageHeight()} bg-slate-800 overflow-hidden`}>
                              {variant.image_path ? (
                                <img
                                  src={getImageUrl(variant.image_path)}
                                  alt={variant.name}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <PhotoIcon className="h-20 w-20 text-slate-600" />
                                </div>
                              )}

                              <div className="absolute top-3 right-3 px-3 py-1.5 rounded-full backdrop-blur bg-slate-900/85 dark:bg-slate-900/85 border border-white/10">
                                <div className="flex items-center gap-1.5">
                                  <ChartBarIcon className="h-4 w-4 text-primary-300" />
                                  <span className="text-sm font-bold text-white tabular-nums">{variant.total_votes}</span>
                                </div>
                              </div>
                            </div>

                            <div className={getCardSpacing()}>
                              <h4 className={`${textSizes.name} font-bold text-gray-900 dark:text-white mb-2 line-clamp-1`}>{variant.name}</h4>

                              {variant.creator_name && (
                                <div className="mb-2">
                                  <span className={`inline-flex items-center rounded-full font-medium ${
                                    gridColumns <= 4 ? 'px-2.5 py-0.5 text-xs' : 'px-2 py-0.5 text-[10px]'
                                  }`}
                                  style={{ background: 'rgba(34,197,94,0.10)', border: '1px solid rgba(34,197,94,0.25)', color: '#86efac' }}>
                                    {gridColumns <= 4 ? `Von ${variant.creator_name}` : variant.creator_name}
                                  </span>
                                </div>
                              )}

                              {gridColumns <= 4 && variant.description && (
                                <p className={`${textSizes.description} text-gray-600 dark:text-slate-400 mb-3 line-clamp-2`}>{variant.description}</p>
                              )}

                              <div className={`rounded-xl mb-3 ${gridColumns <= 4 ? 'p-3' : 'p-2'} bg-primary-500/8 border border-primary-500/20`}>
                                <div className="flex items-baseline gap-1.5">
                                  <span className={`${textSizes.price} font-bold text-primary-700 dark:text-primary-300 tabular-nums`}>
                                    {toNumber(variant.price).toFixed(2)} €
                                  </span>
                                  <span className="text-xs text-gray-600 dark:text-slate-400">pro Stück</span>
                                </div>
                                <div className="text-xs text-gray-600 dark:text-slate-400 mt-1">
                                  Gesamt: <span className="font-semibold text-gray-800 dark:text-slate-200">{(toNumber(variant.price) * item.quantity).toFixed(2)} €</span>
                                </div>
                              </div>

                              {variant.link && (
                                <a
                                  href={variant.link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1.5 text-sm text-primary-700 dark:text-primary-300 hover:text-primary-800 dark:hover:text-primary-200 font-medium mb-4"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <LinkIcon className="h-4 w-4" />
                                  Link öffnen
                                </a>
                              )}

                              <div className="flex gap-2 mt-4">
                                {isVotingClosed(selectedProject.voting_deadline) ? (
                                  <div className={`w-full text-center rounded-xl text-gray-600 dark:text-slate-400 font-medium ${
                                    gridColumns <= 4 ? 'py-3 px-4' : 'py-2 px-3 text-sm'
                                  } bg-gray-100 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08]`}>
                                    Abstimmung beendet
                                  </div>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => handleVote(variant.id, 1)}
                                      className={`flex-1 flex items-center justify-center rounded-xl transition ${
                                        gridColumns <= 4 ? 'py-3 px-4' : 'py-2 px-3'
                                      } ${
                                        variant.my_vote === 1
                                          ? 'bg-primary-500 text-slate-950 shadow-lg'
                                          : 'bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-700 dark:text-slate-300 hover:bg-primary-500/20 hover:text-primary-700 dark:hover:text-primary-200'
                                      }`}
                                      title="Dafür stimmen"
                                      aria-label="Für diese Variante stimmen"
                                      aria-pressed={variant.my_vote === 1}
                                    >
                                      <HandThumbUpIcon className={gridColumns <= 4 ? 'h-6 w-6' : 'h-5 w-5'} />
                                    </button>
                                    <button
                                      onClick={() => handleVote(variant.id, -1)}
                                      className={`flex-1 flex items-center justify-center rounded-xl transition ${
                                        gridColumns <= 4 ? 'py-3 px-4' : 'py-2 px-3'
                                      } ${
                                        variant.my_vote === -1
                                          ? 'bg-rose-500 text-white shadow-lg'
                                          : 'bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-700 dark:text-slate-300 hover:bg-rose-500/20 hover:text-rose-700 dark:hover:text-rose-200'
                                      }`}
                                      title="Dagegen stimmen"
                                      aria-label="Gegen diese Variante stimmen"
                                      aria-pressed={variant.my_vote === -1}
                                    >
                                      <HandThumbDownIcon className={gridColumns <= 4 ? 'h-6 w-6' : 'h-5 w-5'} />
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </article>
            ))}
          </div>
        )}
      </div>

      {/* Varianten-Modal */}
      {showVariantModal && selectedItem && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="rounded-t-2xl sm:rounded-2xl max-w-2xl w-full max-h-[90vh] sm:max-h-[85vh] flex flex-col"
            style={{ background: CARD_BG, border: '1px solid #334155' }}>
            <div className="p-6 border-b border-slate-700">
              <h2 className="text-lg font-bold text-white">Variante hinzufügen zu „{selectedItem.name}"</h2>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Varianten-Name *</label>
                  <input
                    type="text"
                    value={variantForm.name}
                    onChange={(e) => setVariantForm({ ...variantForm, name: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg text-sm bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:ring-2 focus:ring-primary-500 focus:outline-none"
                    placeholder="z.B. Variante A"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Beschreibung</label>
                  <textarea
                    value={variantForm.description}
                    onChange={(e) => setVariantForm({ ...variantForm, description: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg text-sm bg-white/5 border border-white/10 text-white placeholder-slate-500 h-24 focus:ring-2 focus:ring-primary-500 focus:outline-none"
                    placeholder="Beschreibung der Variante..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Preis pro Stück * (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={variantForm.price}
                    onChange={(e) => setVariantForm({ ...variantForm, price: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg text-sm bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:ring-2 focus:ring-primary-500 focus:outline-none"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Link (optional)</label>
                  <input
                    type="url"
                    value={variantForm.link}
                    onChange={(e) => setVariantForm({ ...variantForm, link: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg text-sm bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:ring-2 focus:ring-primary-500 focus:outline-none"
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Bild (optional)</label>

                  <div
                    onPaste={handleVariantPaste}
                    tabIndex={0}
                    className="border-2 border-dashed border-white/10 rounded-xl p-6 mb-3 text-center cursor-pointer hover:border-primary-400 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <PhotoIcon className="h-12 w-12 mx-auto text-slate-500 mb-2" />
                    <p className="text-sm text-slate-300 mb-1">
                      <span className="font-semibold text-primary-300">Klicken Sie hier und drücken Sie Strg+V</span>
                    </p>
                    <p className="text-xs text-slate-500">um ein Bild aus der Zwischenablage einzufügen</p>
                  </div>

                  <div className="text-center text-sm text-slate-500 mb-2">oder</div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleVariantImageChange}
                    className="w-full px-3 py-2 rounded-lg text-sm bg-white/5 border border-white/10 text-slate-300 file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0 file:bg-primary-500 file:text-slate-950 file:text-xs file:font-semibold"
                  />

                  {variantImagePreview && (
                    <div className="mt-4 relative">
                      <img src={variantImagePreview} alt="Vorschau" className="w-full h-48 object-cover rounded-xl" />
                      <button
                        onClick={() => {
                          setSelectedVariantImage(null);
                          setVariantImagePreview(null);
                        }}
                        className="absolute top-2 right-2 p-2 bg-rose-500 hover:bg-rose-600 text-white rounded-full transition-colors"
                        title="Bild entfernen"
                        type="button"
                      >
                        <XCircleIcon className="h-5 w-5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-slate-700 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <button
                onClick={() => {
                  setShowVariantModal(false);
                  setVariantForm({ name: '', description: '', price: '', link: '' });
                  setSelectedVariantImage(null);
                  setVariantImagePreview(null);
                }}
                className="px-4 py-2 text-sm font-semibold text-slate-300 hover:bg-white/5 rounded-xl"
              >
                Abbrechen
              </button>
              <button
                onClick={handleSaveVariant}
                className="px-4 py-2 text-sm font-semibold text-slate-950 bg-primary-500 hover:bg-primary-400 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!variantForm.name || !variantForm.price}
              >
                Variante hinzufügen
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default MyProjects;
